import React, { useState, useEffect, useCallback } from 'react';
import { Question, CreateQuestionDto } from '../../types/exam';
import { examApi } from '../../services/exam.service';
import QuestionForm from './QuestionForm';

interface QuestionManagerProps {
  examId: string;
  examStatus: string;
  totalQuestions: number;
  onRefresh: () => void;
}

export const QuestionManager: React.FC<QuestionManagerProps> = ({
  examId,
  examStatus,
  totalQuestions,
  onRefresh,
}) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);

  const loadQuestions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await examApi.getExamQuestions(examId);
      setQuestions(data);
      setError(null);
    } catch (err) {
      setError('Failed to load questions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [examId]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const handleAddQuestion = async (formData: CreateQuestionDto) => {
    try {
      setLoading(true);
      const newQuestion = await examApi.addQuestion(examId, formData);
      setQuestions([...questions, newQuestion]);
      setShowForm(false);
      setError(null);
      onRefresh();
    } catch (err) {
      setError('Failed to add question');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateQuestion = async (questionId: string, formData: any) => {
    try {
      setLoading(true);
      const updatePayload = {
        questionText: formData.questionText,
        marks: formData.marks,
        difficulty: formData.difficulty,
        options: formData.questionType === 'MCQ' ? formData.options : undefined,
        explanation: formData.explanation,
      };
      const updatedQuestion = await examApi.updateQuestion(examId, questionId, updatePayload);
      setQuestions(
        questions.map((q) => (q.questionId === questionId ? updatedQuestion : q))
      );
      setEditingQuestion(null);
      setShowForm(false);
      setError(null);
      onRefresh();
    } catch (err) {
      setError('Failed to update question');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;

    try {
      setLoading(true);
      await examApi.deleteQuestion(examId, questionId);
      setQuestions(questions.filter((q) => q.questionId !== questionId));
      setError(null);
      onRefresh();
    } catch (err) {
      setError('Failed to delete question');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'EASY':
        return 'text-green-600 bg-green-50';
      case 'MEDIUM':
        return 'text-yellow-600 bg-yellow-50';
      case 'HARD':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getQuestionTypeLabel = (type: string) => {
    switch (type) {
      case 'MCQ':
        return 'Multiple Choice';
      case 'SHORT_ANSWER':
        return 'Short Answer';
      case 'ESSAY':
        return 'Essay';
      case 'TRUE_FALSE':
        return 'True/False';
      case 'MATCHING':
        return 'Matching';
      default:
        return type;
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {showForm ? (
        <QuestionForm
          onSubmit={editingQuestion ? 
            (data) => handleUpdateQuestion(editingQuestion.questionId, data)
            : handleAddQuestion
          }
          onCancel={() => {
            setShowForm(false);
            setEditingQuestion(null);
          }}
          initialData={editingQuestion}
          isEditing={!!editingQuestion}
        />
      ) : (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-800">
              Questions ({questions.length})
            </h3>
            {examStatus === 'DRAFT' && (
              <button
                onClick={() => {
                  setEditingQuestion(null);
                  setShowForm(true);
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded font-medium transition"
              >
                + Add Question
              </button>
            )}
          </div>

          {loading && (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          )}

          {!loading && questions.length === 0 && (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-600 mb-4">No questions added yet</p>
              {examStatus === 'DRAFT' && (
                <button
                  onClick={() => {
                    setEditingQuestion(null);
                    setShowForm(true);
                  }}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded font-medium transition"
                >
                  Add First Question
                </button>
              )}
            </div>
          )}

          {!loading && questions.length > 0 && (
            <div className="grid grid-cols-1 gap-4">
              {questions.map((question) => (
                <div key={question.questionId} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-gray-600">
                          Q{question.sequenceNumber}
                        </span>
                        <span className="text-xs font-medium px-2 py-1 bg-blue-100 text-blue-700 rounded">
                          {getQuestionTypeLabel(question.questionType)}
                        </span>
                        <span className={`text-xs font-medium px-2 py-1 rounded ${getDifficultyColor(question.difficulty)}`}>
                          {question.difficulty}
                        </span>
                        {question.chapter && (
                          <span className="text-xs font-medium px-2 py-1 bg-emerald-100 text-emerald-700 rounded">
                            {question.chapter}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-800">{question.questionText}</p>
                    </div>
                    <div className="ml-4 text-right">
                      <span className="block text-lg font-bold text-gray-700">{question.marks}M</span>
                      {Number(question.negativeMarks || 0) > 0 && (
                        <span className="block text-xs font-semibold text-red-600">-{question.negativeMarks}M</span>
                      )}
                    </div>
                  </div>

                  {question.questionType === 'MCQ' && question.options && (
                    <div className="mb-3 ml-4 space-y-1">
                      {question.options.map((option, idx) => (
                        <p key={idx} className="text-sm text-gray-600">
                          {option.isCorrect ? '✓' : '○'} {option.text}
                        </p>
                      ))}
                    </div>
                  )}

                  {question.explanation && (
                    <div className="mb-3 p-2 bg-blue-50 rounded text-sm text-blue-900">
                      <strong>Explanation:</strong> {question.explanation}
                    </div>
                  )}

                  {examStatus === 'DRAFT' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingQuestion(question);
                          setShowForm(true);
                        }}
                        className="text-blue-500 hover:text-blue-700 text-sm font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteQuestion(question.questionId)}
                        className="text-red-500 hover:text-red-700 text-sm font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuestionManager;
